import { useEffect, useState } from 'react';
import { LuLockKeyhole } from 'react-icons/lu';
import {
  addToast,
  Alert,
  InputOtp,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import CustomButton from '../../../components/CustomButton';
import CustomInput from '../../../components/CustomInput';
import CommonConfirmModal from '../../../components/common/ui/CommonConfirmModal';
import { useAuth } from '../../../../context/AuthContext';
import { handleLogout } from '../../../components/auth/apiAuth';
import { validateVerificationCode } from '../../../../service/twoFactorAuthService';
import {
  changePassword,
  requestChangePasswordOtp,
  resendChangePasswordOtp,
  verifyChangePasswordOtp,
  type ChangePasswordVerificationInfo,
} from '../../../../service/changePasswordService';
import { logError } from '../../../../utils/logger';

const PASSWORD_REGEX =
  /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9\s]).{8,16}$/;

const RESEND_DELAY = 60;

type ChangeStep = 'verify' | 'passwords';

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: {
      data?: {
        message?: string;
        errors?: Array<{ message?: string; msg?: string } | string>;
      };
    };
  };
  const firstError = err?.response?.data?.errors?.[0];
  if (typeof firstError === 'string') return firstError;
  return (
    firstError?.message ||
    firstError?.msg ||
    err?.response?.data?.message ||
    fallback
  );
};

const PasswordChangeSection = () => {
  const { refreshUser } = useAuth();

  const [isPending, setIsPending] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    isOpen: isChangeModalOpen,
    onOpen: onChangeModalOpen,
    onOpenChange: onChangeModalChange,
  } = useDisclosure();

  const [changeStep, setChangeStep] = useState<ChangeStep>('verify');
  const [verificationInfo, setVerificationInfo] =
    useState<ChangePasswordVerificationInfo | null>(null);
  const [securityToken, setSecurityToken] = useState('');
  const [changePasswordToken, setChangePasswordToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCurrentPasswordInvalid, setIsCurrentPasswordInvalid] =
    useState(false);
  const [isNewPasswordInvalid, setIsNewPasswordInvalid] = useState(false);
  const [isConfirmPasswordInvalid, setIsConfirmPasswordInvalid] =
    useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState({ message: '', title: '' });

  const isMfaVerification = verificationInfo?.verificationType === 'mfa';

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const clearForm = () => {
    setChangeStep('verify');
    setVerificationInfo(null);
    setSecurityToken('');
    setChangePasswordToken('');
    setVerificationCode('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsCurrentPasswordInvalid(false);
    setIsNewPasswordInvalid(false);
    setIsConfirmPasswordInvalid(false);
    setIsError(false);
    setError({ message: '', title: '' });
    setResendCooldown(0);
  };

  const initiateVerification = async () => {
    setIsError(false);
    setError({ message: '', title: '' });
    setIsRequestingOtp(true);

    try {
      const response = await requestChangePasswordOtp();
      const data = response?.data;
      setVerificationInfo(data);
      setSecurityToken(data?.securityToken || '');
      setChangeStep('verify');

      if (data?.verificationType === 'email') {
        if (data.resumed) {
          addToast({
            title: 'Verification in progress',
            description:
              response?.message ||
              `Enter the code already sent to ${data?.maskedEmail || 'your email'}.`,
            color: 'primary',
          });
        } else {
          setResendCooldown(RESEND_DELAY);
          addToast({
            title: 'Verification code sent',
            description:
              response?.message ||
              `A code was sent to ${data?.maskedEmail || 'your email'}.`,
            color: 'success',
          });
        }
      }
    } catch (err: unknown) {
      logError('Password change verification request error', { error: err });
      setIsError(true);
      setError({
        title: 'Could not start verification',
        message: getApiErrorMessage(
          err,
          'Something went wrong. Please try again later.'
        ),
      });
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const openChangePasswordModal = () => {
    clearForm();
    onChangeModalOpen();
    void initiateVerification();
  };

  const handleResendCode = async () => {
    if (isMfaVerification || resendCooldown > 0) return;

    setIsError(false);
    setError({ message: '', title: '' });
    setIsResendingOtp(true);

    try {
      const response = await resendChangePasswordOtp();
      const data = response?.data;
      setVerificationInfo(data);
      setSecurityToken(data?.securityToken || '');
      setResendCooldown(RESEND_DELAY);
      addToast({
        title: 'Code resent',
        description: response?.message || 'A new code was sent to your email.',
        color: 'success',
      });
    } catch (err: unknown) {
      logError('Password change resend OTP error', { error: err });
      setIsError(true);
      setError({
        title: 'Could not resend code',
        message: getApiErrorMessage(
          err,
          'Something went wrong. Please try again later.'
        ),
      });
    } finally {
      setIsResendingOtp(false);
    }
  };

  const validateNewPassword = (value: string) => {
    if (!value) {
      setIsNewPasswordInvalid(false);
      return false;
    }
    const valid = PASSWORD_REGEX.test(value);
    setIsNewPasswordInvalid(!valid);
    return valid;
  };

  const validateConfirmPassword = (next: string, candidate: string) => {
    if (!next.trim()) {
      setIsConfirmPasswordInvalid(false);
      return false;
    }
    const valid = candidate.trim() === next.trim();
    setIsConfirmPasswordInvalid(!valid);
    return valid;
  };

  const validateCurrentPassword = (value: string) => {
    if (!value.trim()) {
      setIsCurrentPasswordInvalid(true);
      return false;
    }
    setIsCurrentPasswordInvalid(false);
    return true;
  };

  useEffect(() => {
    if (newPassword) validateNewPassword(newPassword);
  }, [newPassword]);

  useEffect(() => {
    if (confirmPassword || newPassword) {
      validateConfirmPassword(confirmPassword, newPassword);
    }
  }, [confirmPassword, newPassword]);

  const handleContinueToPasswords = async () => {
    setIsError(false);
    setError({ message: '', title: '' });

    if (!validateVerificationCode(verificationCode)) {
      setError({
        title: isMfaVerification
          ? 'Authenticator code required'
          : 'Verification code required',
        message: isMfaVerification
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Enter the 6-digit code sent to your email.',
      });
      setIsError(true);
      return;
    }

    if (!isMfaVerification && !securityToken) {
      setError({
        title: 'Verification session missing',
        message:
          'Please close this dialog and open Change Password again to restore your verification session.',
      });
      setIsError(true);
      return;
    }

    setIsVerifyingOtp(true);

    try {
      const response = await verifyChangePasswordOtp({
        ...(isMfaVerification
          ? { totpCode: verificationCode.trim() }
          : { otp: verificationCode.trim(), securityToken }),
      });

      const token = response?.data?.changePasswordToken;
      if (!token) {
        throw new Error('Verification failed. Please try again.');
      }

      setChangePasswordToken(token);
      setChangeStep('passwords');
    } catch (err: unknown) {
      logError('Password change verification error', { error: err });
      setIsError(true);
      setError({
        title: 'Invalid verification code',
        message: getApiErrorMessage(
          err,
          'The code you entered is invalid or expired. Please try again.'
        ),
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const isSubmitDisabled =
    !currentPassword.trim() ||
    !newPassword.trim() ||
    !confirmPassword.trim() ||
    isCurrentPasswordInvalid ||
    isNewPasswordInvalid ||
    isConfirmPasswordInvalid ||
    currentPassword.trim() === newPassword.trim();

  const handleSubmitChangePassword = async () => {
    setIsError(false);
    setError({ message: '', title: '' });

    if (!validateCurrentPassword(currentPassword)) {
      setError({
        title: 'Current password required',
        message: 'Please enter your current password.',
      });
      setIsError(true);
      return;
    }

    if (!validateNewPassword(newPassword)) {
      setError({
        title: 'Invalid new password',
        message: 'New password does not meet the requirements below.',
      });
      setIsError(true);
      return;
    }

    if (!validateConfirmPassword(confirmPassword, newPassword)) {
      setError({
        title: 'Passwords do not match',
        message: 'New password and confirm password must match.',
      });
      setIsError(true);
      return;
    }

    if (currentPassword.trim() === newPassword.trim()) {
      setError({
        title: 'Same password',
        message: 'New password must be different from your current password.',
      });
      setIsError(true);
      return;
    }

    if (!changePasswordToken) {
      setError({
        title: 'Verification required',
        message: 'Please verify your code before changing your password.',
      });
      setIsError(true);
      setChangeStep('verify');
      return;
    }

    setIsPending(true);

    try {
      const response = await changePassword({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
        changePasswordToken,
      });

      const message =
        response?.message || 'Password has been changed successfully.';

      localStorage.setItem('lastPasswordChange', new Date().toISOString());

      if (message.toLowerCase().includes('please sign in again')) {
        addToast({
          title: 'Password changed',
          description: message,
          color: 'success',
        });
        sessionStorage.clear();
        onChangeModalChange();
        handleLogout();
        return;
      }

      addToast({
        title: 'Password changed',
        description: message,
        color: 'success',
      });
      refreshUser();
      clearForm();
      onChangeModalChange();
    } catch (err: unknown) {
      logError('Password change error', { error: err });
      setIsError(true);
      setError({
        title: 'Could not change password',
        message: getApiErrorMessage(
          err,
          'Something went wrong. Please try again later.'
        ),
      });
    } finally {
      setIsPending(false);
    }
  };

  const isModalBusy =
    isPending || isRequestingOtp || isResendingOtp || isVerifyingOtp;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-3 w-full">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center mr-4 flex-shrink-0">
            <LuLockKeyhole className="text-xl text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800">Password</h3>
            <p className="text-caption mt-[2px]">
              Change your password with email or authenticator verification.
            </p>
          </div>
        </div>

        <div className="flex justify-end w-full sm:w-auto flex-shrink-0">
          <CommonConfirmModal
            buttonLabel="Change Password"
            buttonColor="primary"
            title="Change Password?"
            confirmBtnText="Yes"
            confirmBtnColor="primary"
            description="You will verify your identity before setting a new password. Please note: Resetting your password will sign you out of all active sessions. Once your password has been reset, you'll need to log in again on all devices."
            iconType="info"
            reasonRequired={false}
            reasonMinLength={10}
            data={{}}
            onConfirm={() => {
              openChangePasswordModal();
            }}
          />
        </div>
      </div>

      <Modal
        isOpen={isChangeModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            clearForm();
            onChangeModalChange();
          }
        }}
        placement="center"
        backdrop="blur"
        size="xl"
        scrollBehavior="inside"
        isDismissable={!isModalBusy}
        isKeyboardDismissDisabled={isModalBusy}
      >
        <ModalContent>
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-xl font-bold text-danger-DEFAULT">
                Change Password
              </h2>
              <p className="text-sm font-normal text-default-500">
                {changeStep === 'verify'
                  ? isMfaVerification
                    ? 'Enter the code from your authenticator app.'
                    : verificationInfo?.maskedEmail
                      ? `Enter the code sent to ${verificationInfo.maskedEmail}.`
                      : 'Enter the verification code sent to your email.'
                  : 'Enter your current password, then choose a new password.'}
              </p>
            </ModalHeader>

            <ModalBody>
              {isError && (
                <Alert className="mb-4" color="danger" title={error.title}>
                  {error.message}
                </Alert>
              )}

              {verificationInfo?.resumed && changeStep === 'verify' && (
                <Alert
                  className="mb-4"
                  color="primary"
                  title="Verification in progress"
                >
                  Use the code from your most recent email. A new code was not
                  sent.
                </Alert>
              )}

              {changeStep === 'verify' ? (
                <div className="flex flex-col gap-4 items-start">
                  {isRequestingOtp ? (
                    <p className="text-sm text-default-500">
                      Preparing verification...
                    </p>
                  ) : (
                    <>
                      <div className="w-full">
                        <p className="text-sm font-medium mb-2">
                          {isMfaVerification
                            ? 'Authenticator code'
                            : 'Email verification code'}
                        </p>
                        <InputOtp
                          length={6}
                          value={verificationCode}
                          onValueChange={setVerificationCode}
                          isDisabled={isModalBusy}
                          variant="bordered"
                        />
                      </div>

                      {!isMfaVerification && (
                        <CustomButton
                          label={
                            resendCooldown > 0
                              ? `Resend code in ${resendCooldown}s`
                              : 'Resend code'
                          }
                          variant="light"
                          color="primary"
                          onPress={() => void handleResendCode()}
                          isDisabled={resendCooldown > 0 || isResendingOtp}
                          isLoading={isResendingOtp}
                        />
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <CustomInput
                    label="Current password"
                    type="password"
                    isRequired
                    maxLength={16}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onValueChange={setCurrentPassword}
                    isInvalid={isCurrentPasswordInvalid}
                    errorMessage="Current password is required"
                  />

                  <CustomInput
                    label="New password"
                    type="password"
                    isRequired
                    maxLength={16}
                    placeholder="Example!123"
                    value={newPassword}
                    onValueChange={setNewPassword}
                    isInvalid={isNewPasswordInvalid}
                    errorMessage="Password does not meet requirements"
                  />

                  <CustomInput
                    label="Confirm new password"
                    type="password"
                    isRequired
                    maxLength={16}
                    placeholder="Example!123"
                    value={confirmPassword}
                    onValueChange={setConfirmPassword}
                    isInvalid={isConfirmPasswordInvalid}
                    errorMessage="Passwords do not match"
                  />

                  <div className="bg-primary-50 p-4 rounded border border-primary-200 text-primary-800 flex items-start gap-3 text-sm">
                    <div>
                      <h4 className="font-semibold mb-2">Password rules</h4>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>
                          At least <strong>8 characters</strong>
                        </li>
                        <li>
                          Maximum <strong>16 characters</strong>
                        </li>
                        <li>
                          Include at least <strong>1 number</strong>
                        </li>
                        <li>
                          Include at least <strong>1 lowercase letter</strong>
                        </li>
                        <li>
                          Include at least <strong>1 uppercase letter</strong>
                        </li>
                        <li>
                          Include at least <strong>1 symbol</strong> (e.g.,
                          !@#$%)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <CustomButton
                label="Cancel"
                variant="flat"
                color="danger"
                onPress={onChangeModalChange}
                isDisabled={isModalBusy}
              />
              {changeStep === 'verify' ? (
                <CustomButton
                  label="Verify & Continue"
                  color="primary"
                  onPress={() => void handleContinueToPasswords()}
                  isDisabled={isRequestingOtp || verificationCode.length !== 6}
                  isLoading={isVerifyingOtp}
                />
              ) : (
                <>
                  <CustomButton
                    label="Back"
                    variant="flat"
                    onPress={() => {
                      setChangeStep('verify');
                      setChangePasswordToken('');
                      setIsError(false);
                      setError({ message: '', title: '' });
                    }}
                    isDisabled={isPending}
                  />
                  <CustomButton
                    label="Change Password"
                    color="primary"
                    onPress={() => void handleSubmitChangePassword()}
                    isDisabled={isSubmitDisabled || isPending}
                    isLoading={isPending}
                  />
                </>
              )}
            </ModalFooter>
          </>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PasswordChangeSection;
